import React from "react";
import { ArrowRight, Wallet, Building2, Clock } from "lucide-react";
import { fmtEUR2 } from "@/lib/format";
import { RetroActions } from "@/components/retro/RetroActions";

const Line = ({ label, value, strong, tone = "", testid }) => (
  <div className="flex justify-between gap-3 py-0.5" data-testid={testid}>
    <span className={strong ? "font-semibold" : "text-gray-500"}>{label}</span>
    <b className={tone}>{fmtEUR2(value)}</b>
  </div>
);

const Box = ({ icon: Icon, title, total, children, tone }) => (
  <div className={`rounded-2xl p-4 ${tone}`}>
    <div className="mb-2 flex items-center justify-between gap-2">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide"><Icon className="h-4 w-4" />{title}</p>
      <b className="text-sm">{fmtEUR2(total)}</b>
    </div>
    <div className="space-y-0.5 text-sm">{children}</div>
  </div>
);

export function SensBadge({ value, estimate = false }) {
  if (Math.abs(value || 0) < 0.005) return <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-600">Rien à régler{estimate ? " (estimé)" : ""}</span>;
  const toPrat = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${toPrat ? "bg-violet-50 text-violet-700" : "bg-amber-50 text-amber-800"}`}>
      {toPrat ? "Cabinet" : "Collaborateur"}<ArrowRight className="h-3 w-3" />{toPrat ? "collaborateur" : "cabinet"}{estimate ? " (estimé)" : ""}
    </span>
  );
}

export function RetroSplit({ r, year, month, onChanged }) {
  const m = r.by_mode || {};
  const pid = r.practitioner.id;
  const direct = r.encaisse_direct_mode;
  const cabinetImm = direct ? (m.autre || 0) : (m.cb || 0) + (m.especes || 0) + (m.cheque || 0) + (m.autre || 0) - (r.encaisse_direct || 0);
  return (
    <div className="space-y-3" data-testid={`retro-split-${pid}`}>
      <div className="grid gap-3 md:grid-cols-2">
        <Box icon={Wallet} title={`Encaissé par ${r.practitioner.name}`} total={r.encaisse_direct} tone="bg-violet-50/70 text-violet-900">
          {direct ? <>
            <Line label="CB" value={m.cb} testid={`split-cb-${pid}`} />
            <Line label="Espèces" value={m.especes} testid={`split-especes-${pid}`} />
            <Line label="Chèques" value={m.cheque} testid={`split-cheque-${pid}`} />
          </> : r.encaisse_direct > 0 ? <Line label="CB sur son TPE" value={r.encaisse_direct} /> : <p className="text-xs text-violet-700/70">Aucun encaissement direct : tout est encaissé par le cabinet.</p>}
        </Box>
        <Box icon={Building2} title="Encaissé par le cabinet" total={r.encaisse_cabinet} tone="bg-emerald-50/70 text-emerald-900">
          <Line label="Sécu / C2S (CMU) reçus" value={m.secu} testid={`split-secu-${pid}`} />
          <Line label="Mutuelles reçues" value={m.mutuelle} testid={`split-mutuelle-${pid}`} />
          <Line label="Alma reçus" value={m.alma} testid={`split-alma-${pid}`} />
          {cabinetImm > 0 && <Line label={direct ? "Autres règlements" : "CB, espèces, chèques, autres"} value={cabinetImm} />}
        </Box>
      </div>
      <div className="rounded-2xl bg-gray-50 p-4 text-sm">
        <Line label="CA total réellement encaissé" value={r.ca} strong testid={`split-ca-${pid}`} />
        <Line label={`× Taux de rémunération ${r.taux} %`} value={r.brut} />
        <Line label={`− Déjà perçu directement par ${r.practitioner.name}`} value={-r.encaisse_direct} tone="text-rose-600" />
        <Line label="− Frais et déductions" value={-r.deductions} tone="text-rose-600" />
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2">
          <span className="font-semibold">Solde à ce jour</span>
          <span className="flex items-center gap-2"><SensBadge value={r.solde} /><b className="font-display text-lg" data-testid={`split-solde-${pid}`}>{fmtEUR2(Math.abs(r.solde))}</b></span>
        </div>
        {r.ca_en_attente > 0 && (
          <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50/70 p-3 text-sky-900" data-testid={`split-estimate-${pid}`}>
            <p className="flex items-center gap-1.5 text-xs font-semibold"><Clock className="h-3.5 w-3.5" />Encore attendu ce mois : {fmtEUR2(r.ca_en_attente)}
              <span className="font-normal text-sky-700">(Sécu {fmtEUR2(r.attente_by_kind?.secu)} · Mutuelles {fmtEUR2(r.attente_by_kind?.mutuelle)} · Alma {fmtEUR2(r.attente_by_kind?.alma)})</span></p>
            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
              <span>Estimation fin de mois (CA {fmtEUR2(r.ca_estime)})</span>
              <span className="flex items-center gap-2"><SensBadge value={r.solde_estime} estimate /><b>{fmtEUR2(Math.abs(r.solde_estime))}</b></span>
            </div>
          </div>
        )}
      </div>
      {year && <RetroActions r={r} year={year} month={month} onChanged={onChanged} />}
    </div>
  );
}
